'use client'

import { getGuideStatus, updateGuideStatus } from '@/apis/guideStatus'
import { GuideRegistryKey } from '@/guide/guideRegistry'
import { buildMigratePatchFromLegacyStores } from '@/guide/guideMigration'
import {
  GuideStatusPatch,
  GuideStatusRecord,
  GuideStatusValue,
} from '@/types/guideStatus'
import { atom, selectorFamily, useRecoilCallback, useRecoilValue } from 'recoil'
import { localStorageWriteJsonEffect } from '@/store/util'
import { userState } from '@/store/user'

type SyncStatus = 'clean' | 'dirty' | 'syncing' | 'failed'
type DataState = 'uninitialized' | 'hydrating' | 'ready'

interface PendingPatch {
  id: string
  patch: GuideStatusRecord
  createdAt: number
}

const GUIDE_MIGRATION_FLAG_KEY = GuideRegistryKey.WEB_MIGRATE_DONE_V1
const SYNC_DEBOUNCE_MS = 400

let initializeRequestSeq = 0
let syncDebounceTimer: ReturnType<typeof setTimeout> | null = null
let syncInFlight = false

export const guideStatusState = atom<GuideStatusRecord>({
  key: 'guideStatusState',
  default: {},
  effects: [localStorageWriteJsonEffect('guide_status_data_v1')],
})

const guideStatusValueSelector = selectorFamily<
  GuideStatusValue | undefined,
  GuideRegistryKey
>({
  key: 'guideStatusValueSelector',
  get:
    (key) =>
    ({ get }) => {
      const data = get(guideStatusState)
      return data[key]
    },
})

const guideStatusDataState = atom<DataState>({
  key: 'guideStatusDataState',
  default: 'uninitialized',
})

const guideStatusActiveUserIdState = atom<string | null>({
  key: 'guideStatusActiveUserIdState',
  default: null,
  effects: [localStorageWriteJsonEffect('guide_status_active_user_id_v1')],
})

const guideStatusPendingPatchesState = atom<PendingPatch[]>({
  key: 'guideStatusPendingPatchesState',
  default: [],
})

const guideStatusSyncStatusState = atom<SyncStatus>({
  key: 'guideStatusSyncStatusState',
  default: 'clean',
})

const normalizePatch = (patch: GuideStatusPatch): GuideStatusRecord => {
  const out: GuideStatusRecord = {}
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue
    out[k] = v
  }
  return out
}

const genPatchId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`

const getMergedPatch = (pending: PendingPatch[]) => {
  const merged: GuideStatusRecord = {}
  const idSet = new Set<string>()
  for (const item of pending) {
    idSet.add(item.id)
    Object.assign(merged, item.patch)
  }
  return { merged, idSet }
}

const isInitializeRequestStale = (requestSeq: number) =>
  requestSeq !== initializeRequestSeq

export const useGuideStatus = () => {
  const user = useRecoilValue(userState)
  const guideStatus = useRecoilValue(guideStatusState)
  const dataState = useRecoilValue(guideStatusDataState)
  const syncStatus = useRecoilValue(guideStatusSyncStatusState)
  const activeUserId = useRecoilValue(guideStatusActiveUserIdState)
  const isHydrating = dataState === 'hydrating'
  const ready = user?.user_id
    ? activeUserId === user.user_id && dataState === 'ready'
    : !isHydrating

  const readValue = <T extends GuideStatusValue = GuideStatusValue>(
    key: GuideRegistryKey
  ) => guideStatus[key] as T | undefined

  return {
    ready,
    dataState,
    guideStatus,
    readValue,
    syncStatus,
    isHydrating,
    ...useGuideStatusActions(),
  }
}

export const useGuideStatusActions = () => {
  const getValue = useRecoilCallback(
    ({ snapshot }) =>
      async <T extends GuideStatusValue = GuideStatusValue>(
        key: GuideRegistryKey
      ) => {
        const data = await snapshot.getPromise(guideStatusState)
        return data[key] as T | undefined
      },
    []
  )

  const setLocal = useRecoilCallback(
    ({ set }) =>
      (key: GuideRegistryKey, value: GuideStatusValue) => {
        set(guideStatusState, (prev) => ({ ...prev, [key]: value }))
      },
    []
  )

  const batchSetLocal = useRecoilCallback(
    ({ set }) =>
      (patch: GuideStatusPatch) => {
        const normalized = normalizePatch(patch)
        set(guideStatusState, (prev) => ({ ...prev, ...normalized }))
      },
    []
  )

  const flushPending = useRecoilCallback(
    ({ set, snapshot }) =>
      async () => {
        if (syncInFlight) return

        const currentUser = await snapshot.getPromise(userState)
        if (!currentUser?.user_id) {
          set(guideStatusSyncStatusState, 'dirty')
          return
        }

        const pending = await snapshot.getPromise(
          guideStatusPendingPatchesState
        )
        if (pending.length === 0) {
          set(guideStatusSyncStatusState, 'clean')
          return
        }

        const { merged, idSet } = getMergedPatch(pending)
        if (Object.keys(merged).length === 0) {
          set(guideStatusPendingPatchesState, [])
          set(guideStatusSyncStatusState, 'clean')
          return
        }

        let hasRemaining = false
        syncInFlight = true
        set(guideStatusSyncStatusState, 'syncing')
        try {
          await updateGuideStatus(merged)
          set(guideStatusPendingPatchesState, (prev) => {
            const next = prev.filter((item) => !idSet.has(item.id))
            hasRemaining = next.length > 0
            return next
          })
          set(guideStatusSyncStatusState, hasRemaining ? 'dirty' : 'clean')
        } catch {
          set(guideStatusSyncStatusState, 'failed')
          return
        } finally {
          syncInFlight = false
        }

        if (hasRemaining) {
          flushPending().catch(() => {})
        }
      },
    []
  )

  const batchSetAndSync = useRecoilCallback(
    ({ set }) =>
      async (patch: GuideStatusPatch) => {
        const normalized = normalizePatch(patch)
        if (Object.keys(normalized).length === 0) return

        set(guideStatusState, (prev) => ({ ...prev, ...normalized }))
        set(guideStatusPendingPatchesState, (prev) => [
          ...prev,
          { id: genPatchId(), patch: normalized, createdAt: Date.now() },
        ])
        set(guideStatusSyncStatusState, (prev) =>
          prev === 'syncing' ? 'syncing' : 'dirty'
        )

        if (syncDebounceTimer) {
          clearTimeout(syncDebounceTimer)
        }
        syncDebounceTimer = setTimeout(() => {
          flushPending().catch(() => {})
        }, SYNC_DEBOUNCE_MS)
      },
    [flushPending]
  )

  const setAndSync = useRecoilCallback(
    () => async (key: GuideRegistryKey, value: GuideStatusValue) => {
      await batchSetAndSync({ [key]: value })
    },
    [batchSetAndSync]
  )

  const hydrate = useRecoilCallback(
    ({ set }) =>
      async (keys?: GuideRegistryKey[]) => {
        set(guideStatusDataState, 'hydrating')
        try {
          const remote = await getGuideStatus(keys)
          set(guideStatusState, (prev) => ({ ...prev, ...remote }))
          set(guideStatusDataState, 'ready')
        } catch {
          set(guideStatusDataState, 'ready')
        }
      },
    []
  )

  const initializeForUser = useRecoilCallback(
    ({ set, snapshot }) =>
      async (userId: string) => {
        if (!userId) return

        const requestSeq = ++initializeRequestSeq
        const prevActiveUserId = await snapshot.getPromise(
          guideStatusActiveUserIdState
        )
        const switchedUser = !!prevActiveUserId && prevActiveUserId !== userId

        if (switchedUser) {
          set(guideStatusState, {})
          set(guideStatusPendingPatchesState, [])
          set(guideStatusSyncStatusState, 'clean')
          if (syncDebounceTimer) {
            clearTimeout(syncDebounceTimer)
            syncDebounceTimer = null
          }
        }

        let remote: GuideStatusRecord = {}
        set(guideStatusDataState, 'hydrating')
        set(guideStatusActiveUserIdState, userId)

        try {
          remote = await getGuideStatus()
          if (isInitializeRequestStale(requestSeq)) return

          set(guideStatusState, (prev) => ({ ...prev, ...remote }))
          set(guideStatusDataState, 'ready')
        } catch {
          if (isInitializeRequestStale(requestSeq)) return
          set(guideStatusDataState, 'ready')
        }

        if (isInitializeRequestStale(requestSeq)) return

        const serverMigrated = remote[GUIDE_MIGRATION_FLAG_KEY] === true
        if (!serverMigrated) {
          try {
            const migratePatch = buildMigratePatchFromLegacyStores()
            migratePatch[GUIDE_MIGRATION_FLAG_KEY] = true
            await updateGuideStatus(migratePatch)
            if (isInitializeRequestStale(requestSeq)) return

            set(guideStatusState, (prev) => ({ ...prev, ...migratePatch }))
          } catch {
            if (isInitializeRequestStale(requestSeq)) return
          }
        }

        if (isInitializeRequestStale(requestSeq)) return
        await flushPending()
      },
    [flushPending]
  )

  const syncPending = useRecoilCallback(
    () => async () => {
      if (syncDebounceTimer) {
        clearTimeout(syncDebounceTimer)
        syncDebounceTimer = null
      }
      await flushPending()
    },
    [flushPending]
  )

  const clearError = useRecoilCallback(
    ({ set, snapshot }) =>
      async () => {
        const pending = await snapshot.getPromise(
          guideStatusPendingPatchesState
        )
        set(guideStatusSyncStatusState, pending.length > 0 ? 'dirty' : 'clean')
      },
    []
  )

  const reset = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        if (syncDebounceTimer) {
          clearTimeout(syncDebounceTimer)
          syncDebounceTimer = null
        }
        reset(guideStatusState)
        reset(guideStatusActiveUserIdState)
        reset(guideStatusPendingPatchesState)
        set(guideStatusDataState, 'uninitialized')
        set(guideStatusSyncStatusState, 'clean')
      },
    []
  )

  return {
    getValue,
    setLocal,
    setAndSync,
    batchSetLocal,
    batchSetAndSync,
    hydrate,
    initializeForUser,
    syncPending,
    clearError,
    reset,
  }
}

export const useGuideStatusValue = <
  T extends GuideStatusValue = GuideStatusValue,
>(
  key: GuideRegistryKey
) => {
  return useRecoilValue(guideStatusValueSelector(key)) as T | undefined
}

export const useGuideStatusItem = <
  T extends GuideStatusValue = GuideStatusValue,
>(
  key: GuideRegistryKey
) => {
  const value = useGuideStatusValue<T>(key)
  const { setAndSync } = useGuideStatusActions()

  const setValue = useRecoilCallback(
    () => async (nextValue: T) => {
      await setAndSync(key, nextValue)
    },
    [key, setAndSync]
  )

  return [value, setValue] as const
}
